/**
 * Booking-scoped Airbnb Comms: WhatsApp-style thread + Add/paste import modal.
 * Used by BookingDetail and the Bookings table Comms action modal.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { api } from '../contexts/ApiContext';
import { airbnbConfirmationCodeFromBooking } from '../utils/airbnbConfirmationCode';

export type BookingCommsBookingRef = {
  id: number;
  reservation_url?: string | null;
  notes?: string | null;
  description?: string | null;
  external_booking_id?: string | null;
  guest_name?: string | null;
};

type PendingMedia = {
  client_key: string;
  file_name: string;
  content_type: string;
  data_base64: string;
  preview_url?: string;
};

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const b64 = result.includes(',') ? result.split(',', 1)[1] : result;
      resolve(b64);
    };
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

function formatThemeChipLabel(t: {
  theme_key?: string;
  theme_label?: string;
  confidence?: number | null;
}): string {
  const raw = (t.theme_label || t.theme_key || 'theme').trim();
  const name = raw.includes('_')
    ? raw
        .split('_')
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
    : raw;
  if (t.confidence == null || Number.isNaN(Number(t.confidence))) return name;
  return `${name} (${Number(t.confidence).toFixed(2)})`;
}

/** Load attachment via JWT-backed API into an object URL for <img>/<video>. */
const AuthenticatedMedia: React.FC<{
  url: string;
  contentType?: string;
  fileName?: string;
  mediaKind?: string;
}> = ({ url, contentType, fileName, mediaKind }) => {
  const [src, setSrc] = useState<string | null>(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    (async () => {
      try {
        const res = await api.get(url, { responseType: 'blob', timeout: 60000 });
        objectUrl = URL.createObjectURL(res.data);
        if (!cancelled) setSrc(objectUrl);
      } catch (e: any) {
        if (!cancelled) setErr(e.response?.data?.error || 'Failed to load media');
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  if (err) {
    return (
      <Typography variant="caption" color="error">
        {err}
      </Typography>
    );
  }
  if (!src) {
    return <CircularProgress size={18} sx={{ my: 1 }} />;
  }
  const kind = (mediaKind || '').toLowerCase();
  const ct = (contentType || '').toLowerCase();
  if (kind === 'video' || ct.startsWith('video/')) {
    return (
      <Box
        component="video"
        src={src}
        controls
        sx={{ maxWidth: '100%', maxHeight: 280, borderRadius: 1, display: 'block', mt: 0.5 }}
      />
    );
  }
  return (
    <Box
      component="img"
      src={src}
      alt={fileName || 'attachment'}
      sx={{ maxWidth: '100%', maxHeight: 280, borderRadius: 1, display: 'block', mt: 0.5 }}
    />
  );
};

export type BookingCommsPanelProps = {
  booking: BookingCommsBookingRef;
  /** Optional pre-known confirmation code (otherwise derived from booking). */
  confirmationCode?: string;
  /** Hide the Conversation heading when embedded in a parent dialog title. */
  compactHeader?: boolean;
};

const BookingCommsPanel: React.FC<BookingCommsPanelProps> = ({
  booking,
  confirmationCode,
  compactHeader = false,
}) => {
  const derivedCode = (
    confirmationCode ||
    airbnbConfirmationCodeFromBooking(booking) ||
    ''
  )
    .trim()
    .toUpperCase();

  const [msgConfirmationCode, setMsgConfirmationCode] = useState(derivedCode);
  const [msgPasteText, setMsgPasteText] = useState('');
  const [msgPendingMedia, setMsgPendingMedia] = useState<PendingMedia[]>([]);
  const [msgAddOpen, setMsgAddOpen] = useState(false);
  const [msgThread, setMsgThread] = useState<any | null>(null);
  const [msgLoading, setMsgLoading] = useState(false);
  const [msgImporting, setMsgImporting] = useState(false);
  const [msgError, setMsgError] = useState('');
  const [msgInfo, setMsgInfo] = useState('');

  const loadMessageThread = useCallback(
    async (codeOverride?: string) => {
      const code = (codeOverride || derivedCode || '').trim().toUpperCase();
      if (!code && !booking.id) {
        setMsgThread(null);
        return;
      }
      setMsgLoading(true);
      setMsgError('');
      try {
        const params: Record<string, string | number> = {};
        if (code) params.confirmation_code = code;
        else params.unified_booking_id = booking.id;
        const res = await api.get('/api/airbnb-messages/threads', { params });
        const threads = res.data?.threads || [];
        setMsgThread(threads[0] || null);
        if (threads[0]?.confirmation_code) {
          setMsgConfirmationCode(threads[0].confirmation_code);
        } else if (code) {
          setMsgConfirmationCode(code);
        }
      } catch (err: any) {
        setMsgError(err.response?.data?.error || 'Failed to load message thread');
        setMsgThread(null);
      } finally {
        setMsgLoading(false);
      }
    },
    [booking.id, derivedCode]
  );

  useEffect(() => {
    setMsgConfirmationCode(derivedCode);
    void loadMessageThread(derivedCode);
  }, [booking.id, derivedCode, loadMessageThread]);

  const clearPendingMedia = useCallback(() => {
    setMsgPendingMedia((prev) => {
      prev.forEach((m) => {
        if (m.preview_url) URL.revokeObjectURL(m.preview_url);
      });
      return [];
    });
  }, []);

  const handleRichPaste = async (e: React.ClipboardEvent) => {
    const cd = e.clipboardData;
    if (!cd) return;

    const textPlain = cd.getData('text/plain') || '';
    const html = cd.getData('text/html') || '';
    const fileMap = new Map<string, File>();
    for (const f of Array.from(cd.files || [])) {
      if (f.type.startsWith('image/') || f.type.startsWith('video/')) {
        fileMap.set(`${f.name}:${f.size}:${f.type}`, f);
      }
    }
    for (const item of Array.from(cd.items || [])) {
      if (item.kind === 'file' && (item.type.startsWith('image/') || item.type.startsWith('video/'))) {
        const f = item.getAsFile();
        if (f) fileMap.set(`${f.name}:${f.size}:${f.type}`, f);
      }
    }
    const files = Array.from(fileMap.values());
    const dataUrlMatches = Array.from(
      html.matchAll(/src=["'](data:(image|video)\/[^;]+;base64,[^"']+)["']/gi)
    );

    if (!textPlain && files.length === 0 && dataUrlMatches.length === 0) return;

    e.preventDefault();

    const mediaRows: PendingMedia[] = [];
    for (let i = 0; i < files.length; i += 1) {
      const f = files[i];
      const client_key = `m${Date.now().toString(36)}_${i}`;
      try {
        const data_base64 = await fileToBase64(f);
        mediaRows.push({
          client_key,
          file_name: f.name || `media-${i}`,
          content_type: f.type || 'application/octet-stream',
          data_base64,
          preview_url: URL.createObjectURL(f),
        });
      } catch {
        /* skip unreadable */
      }
    }
    for (let i = 0; i < dataUrlMatches.length; i += 1) {
      const full = dataUrlMatches[i][1];
      const meta = full.match(/^data:([^;]+);base64,(.+)$/i);
      if (!meta) continue;
      const content_type = meta[1];
      const data_base64 = meta[2];
      const client_key = `h${Date.now().toString(36)}_${i}`;
      const ext = content_type.split('/')[1] || 'bin';
      mediaRows.push({
        client_key,
        file_name: `pasted-${i}.${ext}`,
        content_type,
        data_base64,
        preview_url: full,
      });
    }

    let nextText = textPlain;
    const photoStubCount = (nextText.match(/a (photo|video|image) sent by you/gi) || []).length;
    if (mediaRows.length > photoStubCount) {
      const extras = mediaRows.slice(photoStubCount);
      const markers = extras.map((m) => `[[MEDIA:${m.client_key}]]`).join('\n');
      nextText = `${nextText.trim()}\n${markers}`.trim();
    } else if (!nextText.trim() && mediaRows.length) {
      nextText = mediaRows.map((m) => `[[MEDIA:${m.client_key}]]`).join('\n');
    }

    setMsgPasteText((prev) => (prev ? `${prev.trim()}\n\n${nextText}` : nextText));
    setMsgPendingMedia((prev) => [...prev, ...mediaRows]);
  };

  const handleImportMessagePaste = async (replace: boolean) => {
    const code = (msgConfirmationCode || derivedCode || '').trim().toUpperCase();
    if (!code) {
      setMsgError('No confirmation code found for this booking (check notes / reservation URL).');
      return;
    }
    if (!msgPasteText.trim() && msgPendingMedia.length === 0) {
      setMsgError('Paste text or media is required');
      return;
    }
    setMsgImporting(true);
    setMsgError('');
    setMsgInfo('');
    try {
      const res = await api.post(
        '/api/airbnb-messages/threads/paste',
        {
          confirmation_code: code,
          paste_text: msgPasteText,
          replace,
          unified_booking_id: booking.id,
          run_themes: true,
          media: msgPendingMedia.map((m) => ({
            client_key: m.client_key,
            file_name: m.file_name,
            content_type: m.content_type,
            data_base64: m.data_base64,
          })),
        },
        { timeout: 180000 }
      );
      const outcome = res.data?.outcome || 'imported';
      const stats = res.data?.stats || {};
      const skipped = Number(stats.skipped_duplicates || 0);
      const added = Number(stats.added || 0);
      const llmErr = res.data?.theme_result?.llm_error;
      const base =
        skipped > 0
          ? `Imported (${outcome}): added ${added}, skipped ${skipped} duplicate${skipped === 1 ? '' : 's'}.`
          : `Imported (${outcome})${added ? `: added ${added}` : ''}.`;
      setMsgInfo(
        llmErr
          ? `${base} LLM theme error: ${llmErr} (keyword assists may still apply).`
          : base
      );
      setMsgPasteText('');
      clearPendingMedia();
      setMsgAddOpen(false);
      if (res.data?.thread) setMsgThread(res.data.thread);
      else await loadMessageThread(code);
    } catch (err: any) {
      setMsgError(err.response?.data?.error || 'Failed to import paste');
    } finally {
      setMsgImporting(false);
    }
  };

  const handleResuggestThemes = async () => {
    if (!msgThread?.id) return;
    setMsgImporting(true);
    setMsgError('');
    try {
      const res = await api.post(
        `/api/airbnb-messages/threads/${msgThread.id}/suggest-themes`,
        {},
        { timeout: 120000 }
      );
      if (res.data?.thread) setMsgThread(res.data.thread);
      const llmErr = res.data?.theme_result?.llm_error;
      if (llmErr) setMsgInfo(`Themes refreshed with LLM warning: ${llmErr}`);
      else setMsgInfo('Themes refreshed.');
    } catch (err: any) {
      setMsgError(err.response?.data?.error || 'Failed to suggest themes');
    } finally {
      setMsgImporting(false);
    }
  };

  const handleMessageThemeStatus = async (
    messageId: number,
    themeKey: string,
    status: 'accepted' | 'rejected'
  ) => {
    try {
      await api.post(`/api/airbnb-messages/messages/${messageId}/themes`, {
        theme_key: themeKey,
        status,
      });
      await loadMessageThread(msgConfirmationCode);
    } catch (err: any) {
      setMsgError(err.response?.data?.error || 'Failed to update theme');
    }
  };

  const codeLabel = msgConfirmationCode || derivedCode || 'this booking';

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          {!compactHeader && <Typography variant="h6">Conversation</Typography>}
          {compactHeader && booking.guest_name ? (
            <Typography variant="body2" color="text.secondary">
              {booking.guest_name}
              {codeLabel !== 'this booking' ? ` · ${codeLabel}` : ''}
            </Typography>
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            disabled={msgImporting || !msgThread?.id}
            onClick={() => void handleResuggestThemes()}
          >
            Re-run LLM themes
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setMsgError('');
              setMsgAddOpen(true);
            }}
          >
            Add
          </Button>
        </Box>
      </Box>

      {msgError && !msgAddOpen && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMsgError('')}>
          {msgError}
        </Alert>
      )}
      {msgInfo && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMsgInfo('')}>
          {msgInfo}
        </Alert>
      )}
      {msgLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!msgLoading && msgThread?.messages?.length ? (
        <Box
          sx={{
            bgcolor: '#e5ddd5',
            backgroundImage: 'radial-gradient(rgba(0,0,0,0.04) 1px, transparent 1px)',
            backgroundSize: '12px 12px',
            borderRadius: 2,
            p: 2,
            minHeight: 280,
            maxHeight: { xs: '55vh', sm: '60vh' },
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', mb: 0.5 }}>
            Thread #{msgThread.id} · {msgThread.message_count || msgThread.messages.length} messages ·{' '}
            {msgThread.parse_status}
            {msgThread.confirmation_code ? ` · ${msgThread.confirmation_code}` : ''}
          </Typography>
          {msgThread.messages.map((m: any) => {
            const role = (m.sender_role || 'unknown').toLowerCase();
            const isGuest = role === 'guest';
            const isHost = role === 'host';
            const isSystem =
              role === 'system' || m.message_kind === 'system' || m.message_kind === 'meta';
            const name =
              m.sender_name ||
              (isGuest ? msgThread.guest_display_name || 'Guest' : isHost ? 'Host' : 'System');

            if (isSystem) {
              return (
                <Box key={m.thread_message_id || m.id} sx={{ alignSelf: 'center', maxWidth: '85%' }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      textAlign: 'center',
                      bgcolor: 'rgba(255,255,255,0.7)',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      color: 'text.secondary',
                    }}
                  >
                    {m.body}
                  </Typography>
                </Box>
              );
            }

            return (
              <Box
                key={m.thread_message_id || m.id}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: isGuest ? 'flex-start' : 'flex-end',
                  maxWidth: '100%',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: isGuest ? '#075e54' : '#1a237e',
                    mb: 0.25,
                    px: 0.5,
                  }}
                >
                  {name}
                  {m.sent_at_raw ? ` · ${m.sent_at_raw}` : ''}
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    maxWidth: { xs: '92%', sm: '75%' },
                    px: 1.5,
                    py: 1,
                    bgcolor: isGuest ? '#ffffff' : '#dcf8c6',
                    borderRadius: isGuest ? '0 12px 12px 12px' : '12px 0 12px 12px',
                    boxShadow: '0 1px 1px rgba(0,0,0,0.08)',
                  }}
                >
                  {m.body && m.message_kind !== 'media' && (
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: 'pre-wrap', mb: (m.attachments || []).length ? 1 : 0 }}
                    >
                      {m.body}
                    </Typography>
                  )}
                  {(m.attachments || []).map((att: any) => (
                    <AuthenticatedMedia
                      key={att.id}
                      url={att.url}
                      contentType={att.content_type}
                      fileName={att.file_name}
                      mediaKind={att.media_kind}
                    />
                  ))}
                  {m.message_kind === 'media' && !(m.attachments || []).length && (
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                      {m.body || '[media]'}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.25 }}>
                    {(m.themes || [])
                      .filter((t: any) => t.status !== 'rejected')
                      .map((t: any) => {
                        const accepted = t.status === 'accepted';
                        const suggested = t.status === 'suggested';
                        return (
                          <Chip
                            key={t.id || `${t.theme_key}-${t.status}`}
                            size="small"
                            variant={accepted ? 'filled' : 'outlined'}
                            label={formatThemeChipLabel(t)}
                            title={
                              accepted
                                ? 'Accepted — click to reject'
                                : 'Suggested — click to accept, × to reject'
                            }
                            onClick={() =>
                              void handleMessageThemeStatus(
                                m.id,
                                t.theme_key,
                                accepted ? 'rejected' : 'accepted'
                              )
                            }
                            onDelete={() =>
                              void handleMessageThemeStatus(m.id, t.theme_key, 'rejected')
                            }
                            sx={{
                              height: 26,
                              fontWeight: 500,
                              fontSize: '0.72rem',
                              letterSpacing: 0.1,
                              borderRadius: '8px',
                              bgcolor: accepted
                                ? 'rgba(46, 125, 50, 0.14)'
                                : suggested
                                  ? 'rgba(25, 118, 210, 0.06)'
                                  : 'rgba(0,0,0,0.04)',
                              color: accepted ? '#1b5e20' : suggested ? '#1565c0' : 'text.secondary',
                              borderColor: accepted
                                ? 'rgba(46, 125, 50, 0.45)'
                                : suggested
                                  ? 'rgba(25, 118, 210, 0.45)'
                                  : 'divider',
                              borderStyle: suggested ? 'dashed' : 'solid',
                              borderWidth: 1,
                              '& .MuiChip-label': { px: 1 },
                              '& .MuiChip-deleteIcon': {
                                fontSize: 16,
                                color: accepted ? 'rgba(27, 94, 32, 0.55)' : 'rgba(21, 101, 192, 0.55)',
                                '&:hover': {
                                  color: accepted ? '#1b5e20' : '#0d47a1',
                                },
                              },
                              '&:hover': {
                                bgcolor: accepted
                                  ? 'rgba(46, 125, 50, 0.22)'
                                  : 'rgba(25, 118, 210, 0.12)',
                              },
                            }}
                          />
                        );
                      })}
                  </Box>
                </Paper>
              </Box>
            );
          })}
        </Box>
      ) : (
        !msgLoading && (
          <Alert severity="info">
            No message thread yet. Click Add and paste an Airbnb conversation (text + images/videos).
          </Alert>
        )
      )}

      <Dialog
        open={msgAddOpen}
        onClose={() => !msgImporting && setMsgAddOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          Add Airbnb conversation
          <IconButton
            aria-label="Close"
            onClick={() => !msgImporting && setMsgAddOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            size="small"
            disabled={msgImporting}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Paste from the Airbnb host inbox for <strong>{codeLabel}</strong>. Text and clipboard
            images/videos are captured together and stored with the thread.
          </Typography>
          <TextField
            label="Paste Airbnb conversation"
            multiline
            minRows={8}
            fullWidth
            value={msgPasteText}
            onChange={(e) => setMsgPasteText(e.target.value)}
            onPaste={(e) => {
              void handleRichPaste(e);
            }}
            placeholder="Copy from Airbnb Messages and paste here (text + photos/videos)…"
            helperText="Tip: paste once into this field — images/videos on the clipboard are attached automatically."
          />
          {msgPendingMedia.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {msgPendingMedia.length} media file{msgPendingMedia.length === 1 ? '' : 's'} ready to
                upload
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {msgPendingMedia.map((m) => (
                  <Box
                    key={m.client_key}
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'grey.100',
                    }}
                  >
                    {m.content_type.startsWith('video/') ? (
                      <Box
                        component="video"
                        src={m.preview_url}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <Box
                        component="img"
                        src={m.preview_url}
                        alt={m.file_name}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
              <Button size="small" sx={{ mt: 1 }} onClick={clearPendingMedia} disabled={msgImporting}>
                Clear media
              </Button>
            </Box>
          )}
          {msgError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setMsgError('')}>
              {msgError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMsgAddOpen(false)} disabled={msgImporting}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            disabled={msgImporting || msgLoading}
            onClick={() => void handleImportMessagePaste(true)}
          >
            Replace & import
          </Button>
          <Button
            variant="contained"
            disabled={msgImporting || msgLoading}
            onClick={() => void handleImportMessagePaste(false)}
          >
            {msgImporting ? 'Importing…' : 'Import thread'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BookingCommsPanel;
